/**
 * "Done — your request has been submitted" confirmation, shown once right
 * after a ticket is created (NewTicketScreen navigates with
 * `state: { ticketSubmitted: true }`). Mounted by AppShell so it survives the
 * navigation to the new ticket's page.
 *
 * Closes after 5 seconds, on the × button, on Escape, or on a click/tap
 * anywhere outside the card. Closing also clears the router state, so a
 * refresh or Back never shows it again. Display only — nothing is sent.
 */
import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { brandName } from '../api/runtime-config';
import { useEscapeKey, useFocusTrap } from '../ui/hooks';
import { BrandMark } from './icons';
import styles from './SubmittedConfirmation.module.css';

/** How long the confirmation stays up on its own. */
export const SUBMITTED_CONFIRMATION_MS = 5000;

/** Router state that asks for the confirmation. */
export interface TicketSubmittedState {
  ticketSubmitted: true;
}

export function isTicketSubmittedState(state: unknown): state is TicketSubmittedState {
  return typeof state === 'object' && state !== null && (state as { ticketSubmitted?: unknown }).ticketSubmitted === true;
}

export function SubmittedConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const open = isTicketSubmittedState(location.state);
  const cardRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    // Drop the state (keep the URL) so it is shown exactly once.
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null });
  }, [navigate, location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(close, SUBMITTED_CONFIRMATION_MS);
    return () => window.clearTimeout(timer);
  }, [open, close]);

  useFocusTrap(cardRef, open);
  useEscapeKey(close, open);

  if (!open) return null;

  return createPortal(
    <div className={styles.backdrop} onMouseDown={close} data-testid="submitted-confirmation-backdrop">
      <div
        ref={cardRef}
        className={styles.card}
        role="dialog"
        aria-modal="true"
        aria-labelledby="submitted-confirmation-title"
        aria-describedby="submitted-confirmation-text"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button type="button" className={styles.close} aria-label="Close" onClick={close}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4l8 8m0-8l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
        <span className={styles.check} aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <h2 id="submitted-confirmation-title" className={styles.title}>
          Done — your request has been submitted
        </h2>
        <p id="submitted-confirmation-text" className={styles.text}>
          You can track the ticket status from {brandName()}.
        </p>
        <div className={styles.footer}>
          <BrandMark className={styles.brandMark} />
          <span>{brandName()}</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
